// Sichtbarer Bereich (visualViewport) als CSS-Variablen: --vv-top und --vv-height an <html>.
//
// Geht die Bildschirmtastatur auf, schrumpft auf iPhone und iPad nur der sichtbare Bereich – der Layout-Viewport,
// an dem `position: fixed` hängt, bleibt gleich groß, und WebKit schiebt ihn nach oben, um das Feld zu zeigen. Ein
// Blatt mit `fixed inset-0` rutscht dann mit Titel und Schließen-Kreuz unter die Statusleiste, seine feste Fußleiste
// mit der Hauptaktion liegt unter der Tastatur (docs/mobile/SPEZIFIKATION.md §4.8). Dialoge und Drawer legen ihren
// Rahmen darum auf genau den sichtbaren Bereich (`top: var(--vv-top)`, `height: var(--vv-height)`, styles/base.css).
// Ohne Tastatur sind die Werte 0 und die volle Höhe – nichts ändert sich. Android-WebViews werden selbst kleiner.

export interface VisualViewportLike {
  height: number
  offsetTop: number
  addEventListener: (type: 'resize' | 'scroll', listener: () => void) => void
  removeEventListener: (type: 'resize' | 'scroll', listener: () => void) => void
}

/** Schreibt --vv-top/--vv-height und hält sie aktuell. Gibt eine Aufräumfunktion zurück. */
export function watchVisualViewport(
  viewport: VisualViewportLike | null | undefined = typeof window === 'undefined' ? null : window.visualViewport,
  root: HTMLElement | null = typeof document === 'undefined' ? null : document.documentElement,
): () => void {
  if (!viewport || !root) return () => undefined
  let frame = 0
  const apply = (): void => {
    frame = 0
    // ganze Pixel: Bruchteile ließen den Rahmen bei jedem Scroll-Ereignis neu zeichnen
    const top = Math.max(0, Math.round(viewport.offsetTop))
    root.style.setProperty('--vv-top', `${top}px`)
    root.style.setProperty('--vv-height', `${Math.max(0, Math.round(viewport.height))}px`)
    // WebKit hat die Seite hochgeschoben: Unter der Statusleiste läge sonst Seiteninhalt (base.css legt dort Grund hin)
    if (top > 0) root.dataset.vvPanned = ''
    else delete root.dataset.vvPanned
  }
  const schedule = (): void => {
    if (frame) return
    frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(apply) : (apply(), 0)
  }
  apply()
  viewport.addEventListener('resize', schedule)
  viewport.addEventListener('scroll', schedule)
  return () => {
    viewport.removeEventListener('resize', schedule)
    viewport.removeEventListener('scroll', schedule)
    if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame)
    root.style.removeProperty('--vv-top')
    root.style.removeProperty('--vv-height')
    delete root.dataset.vvPanned
  }
}
